import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Box, TextField, Snackbar, Alert, IconButton } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import bcrypt from 'bcryptjs';
import UserListView from './UserListView';

const modernicTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: "20px", // Rounded buttons
                    padding: "10px 20px", // Button padding
                    backgroundColor: "#333", // Darker button background
                    color: "#fff", // White text for contrast
                    "&:hover": {
                        backgroundColor: "#555", // Slightly lighter background on hover
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: "10px", // Rounded text fields
                    marginBottom: "20px", // Space below the text field
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: "#444", // Darker background for text fields
                        "& fieldset": {
                            borderColor: "#666", // Lighter border color for text field
                        },
                        "&:hover fieldset": {
                            borderColor: "#888", // Border color on hover
                        },
                    },
                    "& .MuiInputLabel-root": {
                        color: "#bbb", // Lighter label color for text fields
                    },
                    "& .MuiInputBase-input": {
                        color: "#fff", // White text in input
                    },
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    borderRadius: "10px", // Rounded corners for snackbar
                    backgroundColor: "#333", // Darker background for snackbar
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    backgroundColor: "#444", // Dark background for alert
                    color: "#fff", // White text in alert
                },
            },
        },
    },
    palette: {
        background: {
            default: "linear-gradient(135deg, #1f1f1f 0%, #333 100%)", // Dark gradient from lighter to darker
        },
        text: {
            primary: "#fff", // Ensuring text stays white for contrast against the dark background
        },
    },
});

const GenReferral = () => {
    const token = localStorage.getItem("calendar_admin");
    const user_Id = localStorage.getItem("userId");

    const [referralCode, setReferralCode] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Function to generate a unique referral code using bcrypt
    const generateReferralCode = () => {
        const timestamp = Date.now(); // Use current timestamp
        const data = `${user_Id}-${timestamp}`; // Combine data for uniqueness

        // Generate the hashed referral code using bcrypt
        const salt = bcrypt.genSaltSync(10); // Generate a salt for bcrypt
        const hash = bcrypt.hashSync(data, salt); // Hash the combined data

        // Get the first 10 characters of the hash as the referral code
        const referralCode = hash.substring(0, 10).toUpperCase();
        return referralCode;
    };

    // Function to check if the generated referral code is unique
    const checkUniqueReferralCode = async (code) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/check_referral_code/${code}/${user_Id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data.exists;  // Returns whether the code already exists
        } catch (error) {
            console.error("Error checking referral code uniqueness:", error.message);
            return true;  // If there is an error, assume the code is not unique
        }
    };

    // Generate unique referral code
    const generateUniqueReferralCode = async () => {
        setIsGenerating(true);
        let count = 0;
        let newCode;
        let isUnique = false;

        // Loop until a unique code is found
        while (!isUnique) {
            newCode = generateReferralCode();
            isUnique = !(await checkUniqueReferralCode(newCode)); // Ensure the code is unique
            // isUnique = true;
            if (count++ > 10) {
                setIsGenerating(false);
                return;
            }
        }

        setReferralCode(newCode);
        setIsGenerating(false);
    };

    // Copy referral code to clipboard
    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(`${process.env.REACT_APP_API_BASE_URL}/signup?referralCode=${referralCode}`);
        setOpenSnackbar(true);  // Open the snackbar when copied
    };

    // Handle Snackbar close
    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    return (
        <Box sx={{
            width: '100%',
            // height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2
        }}>
            <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Referral Code TextField with Copy Icon */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Referral Code"
                        value={referralCode==''?'':`${process.env.REACT_APP_API_BASE_URL}/signup?referralCode=${referralCode}`}
                        fullWidth
                        variant="outlined"
                        disabled
                        sx={{
                            backgroundColor: "#444", // Dark background for the input field
                            color: "#fff", // White text inside
                            '& .MuiInputLabel-root': {
                                color: "#bbb", // Lighter color for label
                            },
                            '& .MuiOutlinedInput-root': {
                                color: "#fff", // White text for the input field
                                '& fieldset': {
                                    borderColor: "#666", // Light border color
                                },
                                '&:hover fieldset': {
                                    borderColor: "#888", // Darker border on hover
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: "#fff", // Ensure the text is white
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={handleCopyToClipboard}
                                    sx={{
                                        position: 'absolute',
                                        right: '10px', // Position the copy icon at the end
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: "#fff", // Icon color
                                    }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            ),
                        }}
                    />

                    {/* Generate Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={generateUniqueReferralCode}
                        sx={{
                            borderRadius: "20px", // Rounded button
                            padding: "10px 20px", // Button padding
                            backgroundColor: "#333", // Dark button background
                            color: "#fff", // Button text color
                            '&:hover': {
                                backgroundColor: "#555", // Lighter background on hover
                            },
                        }}
                        fullWidth
                        disabled={isGenerating} // Disable the button while generating
                    >
                        {isGenerating ? 'Generating...' : 'Generate Referral Code'}
                    </Button>

                    {/* Snackbar for Confirmation */}
                    <Snackbar
                        open={openSnackbar}
                        autoHideDuration={3000}
                        onClose={handleSnackbarClose}
                    >
                        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                            Referral url copied to clipboard!
                        </Alert>
                    </Snackbar>
                </Box>
            </Box>
            <UserListView refetch = {isGenerating}/>
        </Box>
    );
};

export default GenReferral;
